import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, CircularProgress, Typography, Box,
} from '@mui/material';
import { axios, baseUrl } from 'Api';

// Affiliate "Customize invite email" — lets affiliates whose plan includes the
// custom_email_template feature set the subject / intro / signature of the invite
// email sent to new users. The Access-Projector button, set-password link and
// fallback link are always appended by the backend and can't be edited here.
//
// Load:  POST /api/affiliate/invite-email        { token }
// Save:  POST /api/affiliate/invite-email/save   { token, subject, intro, signature }
// (token is injected by the axios interceptor.)
const InviteEmailModal = ({ open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [defaultSubject, setDefaultSubject] = useState('Welcome to Projector');
  const [subject, setSubject] = useState('');
  const [intro, setIntro] = useState('');
  const [signature, setSignature] = useState('');
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      setOkMsg('');
      try {
        const { data } = await axios.post(`${baseUrl}/affiliate/invite-email`, {});
        if (!active) return;
        if (data?.success) {
          setEnabled(Boolean(data.enabled));
          setSubject(data.subject || '');
          setIntro(data.intro || '');
          setSignature(data.signature || '');
          setDefaultSubject(data.default_subject || 'Welcome to Projector');
        } else {
          setError(data?.message || 'Could not load email settings.');
        }
      } catch (e) {
        if (active) setError(e?.response?.data?.message || 'Could not load email settings.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setOkMsg('');
    try {
      const { data } = await axios.post(`${baseUrl}/affiliate/invite-email/save`, {
        subject, intro, signature,
      });
      if (data?.success) {
        setOkMsg(data?.message || 'Invite email updated.');
        onSaved?.();
        setTimeout(() => { onClose?.(); }, 700);
      } else {
        setError(data?.message || 'Could not save invite email.');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save invite email.');
    } finally {
      setSaving(false);
    }
  };

  const font = { fontFamily: 'Montserrat, sans-serif' };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: 16 } }}
    >
      <DialogTitle sx={{ ...font, fontWeight: 700, fontSize: 18 }}>Customize invite email</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !enabled ? (
          <Typography sx={{ ...font, color: '#6b7280', fontSize: 14 }}>
            Custom email is not enabled for your plan.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ ...font, fontSize: 12.5, color: '#6b7280' }}>
              These appear in the invite email new users receive. The “Access Projector”
              button and the set-password link are always added automatically.
            </Typography>
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={defaultSubject}
              helperText={`Leave blank to use “${defaultSubject}”.`}
              fullWidth
              size="small"
              InputProps={{ style: font }}
            />
            <TextField
              label="Intro / welcome message"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              placeholder="Shown above the Access Projector button"
              fullWidth
              multiline
              minRows={3}
              size="small"
              InputProps={{ style: font }}
            />
            <TextField
              label="Signature"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Shown below the button (your name / contact)"
              fullWidth
              multiline
              minRows={2}
              size="small"
              InputProps={{ style: font }}
            />
            {error && <Typography sx={{ ...font, color: '#dc2626', fontSize: 13 }}>{error}</Typography>}
            {okMsg && <Typography sx={{ ...font, color: '#059669', fontSize: 13 }}>{okMsg}</Typography>}
          </Box>
        )}
        {!loading && !enabled && error && (
          <Typography sx={{ ...font, color: '#dc2626', fontSize: 13, mt: 1 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ ...font, textTransform: 'none' }}>
          Close
        </Button>
        {enabled && !loading && (
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{
              ...font,
              textTransform: 'none',
              borderRadius: 2,
              background: 'linear-gradient(180deg, #2a2d36 0%, #15171c 100%)',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InviteEmailModal;
